import { useState, useEffect, useRef } from 'react';
import { DollarSign } from 'lucide-react';

/**
 * CurrencyMenu Component
 * 
 * A reusable currency menu component that provides currency conversion functionality
 * with dropdown interface and various currency options.
 * 
 * Props:
 * - onCurrencyChange: Callback function called when currency option changes
 * - className: Additional CSS classes for styling
 * 
 * Expected structure for window.currencyRates:
 * {
 *   GBP: 0.85090001,
 *   ILS: 4.1816700319,
 *   USD: 1.1545609338,
 *   EUR: 1
 * }
 * 
 * Currency options include:
 * - local: המטבע המקומי (original currency each locale came with)
 * - EUR: Euro (base currency)
 * - USD: US Dollar
 * - GBP: British Pound
 * - ILS: Israeli Shekel
 */
function CurrencyMenu({ onCurrencyChange, className = '' }) {
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [selectedCurrency, setSelectedCurrency] = useState('local');
  const currencyButtonRef = useRef(null);

  // Currency options with Hebrew labels
  const currencyOptions = [
    { value: 'local', label: 'המטבע המקומי', symbol: '💱' },
    { value: 'EUR', label: 'יורו (EUR)', symbol: '€' },
    { value: 'USD', label: 'דולר (USD)', symbol: '$' },
    { value: 'GBP', label: 'פאונד (GBP)', symbol: '£' },
    { value: 'ILS', label: 'שקל (ILS)', symbol: '₪' }
  ];

  // Close currency menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCurrencyMenu && !event.target.closest('.currency-menu-container')) {
        setShowCurrencyMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCurrencyMenu]);

  const toggleCurrencyMenu = () => {
    if (!showCurrencyMenu && currencyButtonRef.current) {
      // Calculate position when opening
      const rect = currencyButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    
    setShowCurrencyMenu(!showCurrencyMenu);
  };

  const handleCurrencyChange = (currencyValue) => {
    setSelectedCurrency(currencyValue);
    setShowCurrencyMenu(false);
    
    // Get currency rates from window
    const rates = window.currencyRates || {};
    
    // Call the callback to notify parent component about currency change
    if (onCurrencyChange) {
      onCurrencyChange(currencyValue, rates);
    }
  };



  // Get the selected option details for display
  const selectedOption = currencyOptions.find(option => option.value === selectedCurrency) || currencyOptions[0];

  return (
    <div className={`relative currency-menu-container ${className}`}>
      <div 
        ref={currencyButtonRef}
        className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
        onClick={toggleCurrencyMenu}
      >
        <span className="text-sm text-gray-700">מטבע</span>
        <DollarSign className="w-5 h-5 text-gray-600" />
      </div>
      
      {/* Currency Dropdown Menu */}
      {showCurrencyMenu && (
        <div 
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[180px] z-[9999]"
          style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 9999,
            minWidth: '180px'
          }}
        >
          {currencyOptions.map((option, index) => (
            <div 
              key={option.value}
              className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                index < currencyOptions.length - 1 ? 'border-b border-gray-100' : ''
              } ${
                selectedCurrency === option.value 
                  ? 'bg-blue-50 text-blue-700 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleCurrencyChange(option.value)}
            >
              <div className="flex items-center justify-between">
                <span>{option.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs opacity-70">{option.symbol}</span>
                  {selectedCurrency === option.value && (
                    <span className="text-blue-600">✓</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CurrencyMenu; 